import type { JSXChild, JSXProps } from 'engine/index';
import { Icon } from './Icon';
import type { FocusFieldEvent } from './FormComponents';

export function TabView(props: JSXProps, children: HTMLElement[]) {
	const view: HTMLElement = (
		<div {...props}>
			<div
				class="border-b border-gray-100 bg-gray-50"
				tab-buttons
				on:click={(e: MouseEvent) => {
					const tabName = (e.target as HTMLElement).closest('[for-tab]')?.getAttribute('for-tab');
					if (tabName) selectTab(tabName);
				}}
			></div>
			<div tab-view class="p-4">
				{children}
			</div>
		</div>
	);

	const tabs = [...view.querySelectorAll('[tab-view]>[tab]')].map((el) => {
		return {
			name: el.getAttribute('tab'),
			label: el.getAttribute('tab-label'),
			icon: el.getAttribute('tab-icon'),
		};
	}) as { name: string; label: string; icon?: string }[];

	let prevSelectedTab: HTMLElement | null = null;
	let prevSelectedBtn: HTMLElement | null = null;

	function selectTab(name: string) {
		if (name === '') return;
		const newTab = view.querySelector(`[tab-view]>[tab=${name}]`) as HTMLElement;
		const newBtn = view.querySelector(`[tab-buttons]>[for-tab=${name}]`) as HTMLElement;

		if (newTab && newBtn) {
			prevSelectedTab?.removeAttribute('data-activetab');
			newTab?.setAttribute('data-activetab', '');
			prevSelectedTab = newTab;

			prevSelectedBtn?.removeAttribute('data-active');
			newBtn?.setAttribute('data-active', '');
			prevSelectedBtn = newBtn;
		}
	}

	const tabBtns = view.querySelector('[tab-buttons]');
	tabBtns?.append(
		...tabs.map(({ name, label, icon }) => {
			return (
				<button
					type="button"
					for-tab={name}
					class="inline-flex items-center gap-2 px-4 py-2 text-gray-700 data-[active]:bg-white data-[active]:text-gray-950"
				>
					{icon && <Icon icon={icon} class="size-4 text-gray-700" />}
					{label}
				</button>
			);
		}),
	);
	selectTab(tabs[0].name);

	const tabView = view.querySelector('[tab-view]') as HTMLElement;
	// @ts-expect-error custom event
	tabView.addEventListener(
		'focus-field',
		(e: FocusFieldEvent) => {
			const tab = e.field.closest('[tab]') as HTMLElement;
			if (tab) {
				const tabBtn = tab
					.closest('[tab-view]')
					?.parentElement?.querySelector(`[for-tab="${tab.getAttribute('tab')}"]`) as HTMLButtonElement | null;
				tabBtn?.click();
			}
		},
		true,
	);

	return view;
}

type TabProps = {
	name: string;
	label: string;
	icon?: string;
} & JSXProps;

export function Tab({ name, label, icon, ...props }: TabProps, children: JSXChild[]) {
	return (
		<div {...props} tab={name} tab-label={label} tab-icon={icon} class="hidden data-[activetab]:block">
			{children}
		</div>
	);
}
